import React, { useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { StationProvisioning } from "../../../../hooks/useStationProvisioning";
import { rejectProvisioning } from "../../../../services/eroundProvisioning";
import { ProspectDetail } from "../../../../types/onboarding";
import {
  EroundSetupResult,
  EroundSetupStep,
  clearingCustomerText,
  runEroundSetup,
  setupPlan,
} from "../../../../utils/eroundSetup";
import { MAX_REASON } from "./ProvisioningDialog";
import { provisioningErrorMessage } from "./provisioningErrors";

interface EroundSetupDialogProps {
  prospect: ProspectDetail;
  stationIds: string[];
  byStation: Record<string, StationProvisioning>;
  visible: boolean;
  onHide: () => void;
  // Re-reads the prospect and the stations' eRound status after a run.
  onChanged: () => Promise<unknown> | void;
}

const STEP_LABELS: Record<EroundSetupStep, string> = {
  "access-data": "Confirm the access data",
  promote: "Promote the customer",
  approve: "Send the stations to eRound",
};

// A step Approve runs, not a choice (CWA-247): its number while pending, then a check or a cross.
const StepLine: React.FC<{ number: number; label: string; done: boolean; failed: boolean }> = ({
  number,
  label,
  done,
  failed,
}) => (
  <li className="flex align-items-center gap-2 mb-1">
    {failed || done ? (
      <i className={`pi ${failed ? "pi-times-circle text-red-500" : "pi-check-circle text-green-500"}`} aria-hidden />
    ) : (
      <span
        className="inline-flex align-items-center justify-content-center border-round surface-200 text-700 text-xs font-semibold"
        style={{ width: "1.25rem", height: "1.25rem" }}
        aria-hidden
      >
        {number}
      </span>
    )}
    <span>{label}</span>
    {done && <span className="text-500 text-sm">done</span>}
    {failed && <span className="text-red-500 text-sm">failed</span>}
  </li>
);

/**
 * "Set up in eRound + OCPP" (CWA-231): one approval that confirms the access
 * data, promotes the customer and sends every waiting station to eRound, in that
 * order. A failed step stops the run and says why; Approve again picks up there.
 */
const EroundSetupDialog: React.FC<EroundSetupDialogProps> = ({ prospect, stationIds, byStation, visible, onHide, onChanged }) => {
  const plan = setupPlan(prospect.current_state, stationIds, byStation);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<EroundSetupResult | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [rejectError, setRejectError] = useState<string | null>(null);
  const trimmed = reason.trim();
  const finished = result !== null && result.failedAt === null;
  const nothingToDo = plan.accessDataDone && plan.promoted && plan.approvals.length === 0;

  const approve = async () => {
    setRunning(true);
    setResult(null);
    let run: EroundSetupResult | null = null;
    try {
      run = await runEroundSetup(prospect.id, plan);
      setResult(run);
      await onChanged();
    } finally {
      setRunning(false);
    }
    // CWA-248: the reload brings this dialog back fresh, without the run's result, so a finished run closes it.
    // The page's header shows the eRound progress. A failed run stays open with its error.
    if (run.failedAt === null) onHide();
  };

  // Declines every waiting station: each is then set up in eRound by hand.
  const reject = async () => {
    setRunning(true);
    setRejectError(null);
    try {
      for (const { stationId } of plan.approvals) {
        await rejectProvisioning(stationId, trimmed);
      }
      setRejecting(false);
      setReason("");
      await onChanged();
    } catch (err) {
      setRejectError(provisioningErrorMessage(err, "reject"));
    } finally {
      setRunning(false);
    }
  };

  const footer = (
    <div className="flex flex-wrap justify-content-end gap-2">
      {!finished && !rejecting && plan.approvals.length > 0 && (
        <Button label="Reject…" severity="danger" outlined disabled={running} onClick={() => setRejecting(true)} />
      )}
      {!finished && !rejecting && !nothingToDo && (
        <Button label="Approve" icon="pi pi-check" loading={running} disabled={running} onClick={approve} />
      )}
      <Button label="Close" severity="secondary" outlined disabled={running} onClick={onHide} />
    </div>
  );

  const stationsWord = plan.approvals.length === 1 ? "the station" : `${plan.approvals.length} stations`;

  return (
    <Dialog
      header="Set up in eRound + OCPP"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "44rem" }}
      breakpoints={{ "960px": "90vw" }}
    >
      {result?.failedAt && (
        <Message severity="error" className="w-full mb-3" text={`Stopped at "${STEP_LABELS[result.failedAt]}": ${result.message}`} />
      )}
      {finished && (
        <Message
          severity="success"
          className="w-full mb-3"
          text={
            result?.approved.length
              ? "Done. The customer is promoted and the stations are on their way to eRound."
              : "Done. The customer is promoted."
          }
        />
      )}

      <p className="mt-0">{clearingCustomerText(prospect)}</p>

      <p className="mb-2 text-600">Approve runs these steps, in order:</p>
      <ol className="list-none p-0 m-0 mb-3" aria-label="Steps">
        <StepLine
          number={1}
          label={STEP_LABELS["access-data"]}
          done={plan.accessDataDone}
          failed={result?.failedAt === "access-data"}
        />
        <StepLine number={2} label={STEP_LABELS.promote} done={plan.promoted} failed={result?.failedAt === "promote"} />
        <StepLine
          number={3}
          label={plan.approvals.length ? `Send ${stationsWord} to eRound` : "No station is waiting for approval"}
          done={plan.approvals.length === 0}
          failed={result?.failedAt === "approve"}
        />
      </ol>

      {plan.approvals.length > 0 && (
        <section className="mb-3" aria-label="What eRound gets">
          <h4 className="mt-0 mb-2">What eRound gets</h4>
          {plan.approvals.map(({ stationId, record }) => {
            const request = record.request;
            const mapping = record.eroundMapping;
            return (
              <div key={stationId} className="surface-50 border-round p-2 mb-2">
                <div className="font-semibold">{stationId}</div>
                {request && (
                  <div className="text-sm">
                    {request.location.locationName} · {request.location.address}
                  </div>
                )}
                <div className="text-sm">
                  {mapping ? `eRound model: ${mapping.manufacturer} – ${mapping.model}` : request ? `${request.station.manufacturer} – ${request.station.model}` : null}
                  {request && ` · ${request.station.numberOfChargingPoints} charging point${request.station.numberOfChargingPoints === 1 ? "" : "s"}`}
                  {` · OCPP key ${record.ocppSecretPresent ? "present" : "missing"}`}
                </div>
              </div>
            );
          })}
        </section>
      )}

      {plan.byHand.length > 0 && (
        <Message
          severity="info"
          className="w-full mb-3"
          text={`${plan.byHand.join(", ")} ${plan.byHand.length === 1 ? "is" : "are"} not sent by this approval: set ${plan.byHand.length === 1 ? "it" : "them"} up in eRound by hand.`}
        />
      )}

      {rejecting && (
        <section>
          <label htmlFor="eround-setup-reject-reason" className="block mb-2">
            Why are these stations set up by hand?
          </label>
          <InputTextarea
            id="eround-setup-reject-reason"
            className="w-full"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          {rejectError && <Message severity="error" className="w-full mt-2" text={rejectError} />}
          <div className="flex justify-content-between align-items-center mt-2">
            <span className={trimmed.length > MAX_REASON ? "text-red-500" : "text-500"}>
              {trimmed.length} / {MAX_REASON}
            </span>
            <div className="flex gap-2">
              <Button label="Cancel" severity="secondary" text disabled={running} onClick={() => setRejecting(false)} />
              <Button
                label="Reject"
                severity="danger"
                loading={running}
                disabled={running || !trimmed || trimmed.length > MAX_REASON}
                onClick={reject}
              />
            </div>
          </div>
        </section>
      )}
    </Dialog>
  );
};

export default EroundSetupDialog;
